import { Chart as ChartJS } from 'chart.js'
import { defineComponent, h } from 'vue'
import _ from 'lodash'
import { registerChartComponents } from './chartSetup'

const parseChartNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (value == null) return value
  if (typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'value')) {
      return parseChartNumber(value.value)
    }
    return value
  }
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : value
}

export default defineComponent({
  name: 'HorizontalBarChart',
  props: {
    chartData: {
      type: Object,
      default: () => ({})
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      chart: null,
      renderFrame: null
    }
  },
  computed: {
    parsedChartData () {
      const dataClone = _.cloneDeep(this.chartData)
      if (dataClone && Array.isArray(dataClone.datasets)) {
        dataClone.datasets = dataClone.datasets.map(dataset => {
          if (Array.isArray(dataset.data)) {
            dataset.data = dataset.data.map(parseChartNumber)
          }
          return dataset
        })
      }
      return dataClone
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler () {
        this.renderChart()
      }
    },
    options: {
      deep: true,
      handler () {
        this.renderChart()
      }
    }
  },
  mounted () {
    registerChartComponents()
    this.renderChart()
  },
  beforeUnmount () {
    if (this.renderFrame) {
      cancelAnimationFrame(this.renderFrame)
      this.renderFrame = null
    }
    this.destroyChart()
  },
  methods: {
    destroyChart () {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },
    renderChart () {
      if (this.renderFrame) {
        cancelAnimationFrame(this.renderFrame)
      }
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = null

        const canvas = this.$refs.canvas
        if (!canvas || !canvas.isConnected) return

        const context = canvas.getContext && canvas.getContext('2d')
        if (!context) return

        const chartData = this.parsedChartData
        if (!chartData || !Array.isArray(chartData.labels) || !chartData.labels.length) {
          this.destroyChart()
          return
        }

        this.destroyChart()

        const optionsCopy = _.cloneDeep(this.options || {})
        if (
          optionsCopy.scales &&
          optionsCopy.scales.x &&
          !Number.isFinite(optionsCopy.scales.x.max)
        ) {
          delete optionsCopy.scales.x.max
        }
        const options = {
          animation: false,
          ...optionsCopy,
          indexAxis: 'y'
        }
        this.chart = new ChartJS(context, {
          type: 'bar',
          data: chartData,
          options
        })
      })
    }
  },
  render () {
    return h('canvas', { ref: 'canvas' })
  }
})
